// BookingHeader.jsx
import React from "react";
import { IconButton } from "@mui/material";
import { ArrowBack, CalendarMonth, Event, LocationOn, Person, Phone } from "@mui/icons-material";
import { parse, isValid, format, addMinutes } from "date-fns";
import { StyledBox, StyledTypography } from "../../../assets/css/styledComponent";
import { InfoRow, formatDuration } from "../../../util/utils";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";

// Updated getFormattedTimeRange function with a guard for undefined input
export function getFormattedTimeRange(time, length) {
  if (!time) {
    console.error("No time provided to getFormattedTimeRange");
    return "Invalid time";
  }
  
  let parsedDate;
  if (typeof time === "string") {
    const normalizedTime = time.toUpperCase();
    parsedDate = parse(normalizedTime, "yyyy-MM-dd'T'h:mma", new Date());
  } else if (time instanceof Date) {
    parsedDate = time;
  } else {
    console.error("Invalid time type:", time);
    return "Invalid time";
  }

  if (!isValid(parsedDate)) {
    console.error("Parsed date is invalid:", parsedDate);
    return "Invalid time";
  }

  const startTime = format(parsedDate, "h:mm a").toLowerCase();
  const endTime = format(addMinutes(parsedDate, length), "h:mm a").toLowerCase();

  return `${startTime} - ${endTime}`;
}

const BookingHeader = ({ bookingData, parsedTime, storedTimeZone, staff, navigate }) => { 
  // Guard against undefined parsedTime
  if (!parsedTime || !isValid(parsedTime)) {
    console.error("BookingHeader received an invalid parsedTime:", parsedTime);
    return (
      <StyledBox sx={{ padding: "20px" }}>
        <StyledTypography variant="h6">Invalid booking time</StyledTypography>
      </StyledBox>
    );
  }

  // Use getFormattedTimeRange with the provided parsedTime (which is a Date)
  const timeRange = getFormattedTimeRange(parsedTime, bookingData.length);
  const date = isValid(parsedTime) ? format(parsedTime, "EEEE, MMMM d, yyyy") : "Invalid date";

  return (
    <StyledBox sx={{ flex: 1, padding: "20px", display: "flex", flexDirection: "column", gap: "15px" }}>
      <StyledBox sx={{ display: "flex", alignItems: "center" }}>
        <IconButton onClick={() => navigate(-1)} sx={{ border: "1px solid lightgrey" }}>
          <ArrowBack />
        </IconButton>
      </StyledBox>
      <StyledTypography fontSize="20px" style={{ overflowWrap: "break-word" }}>
        {bookingData.title}
      </StyledTypography>
      <InfoRow
        icon={<CalendarMonth />}
        text={
          <>
            {timeRange} <strong>({formatDuration(bookingData.length)})</strong>
          </>
        }
      />
      <InfoRow icon={<Event />} text={date} />
      {bookingData.location && <InfoRow icon={<LocationOn />} text={bookingData.location} />}
      {Number(bookingData.meetingLocationTypeId) === 2 && (
        <InfoRow icon={<Phone />} text="Check your email/SMS for the phone number." />
      )}
      {bookingData.price && <InfoRow icon={<AttachMoneyIcon />} text={"$ " + bookingData.price} />}
      <InfoRow icon={<Person />} text={`Provider: ${staff?.name || "Provider"}`} />
      <InfoRow text={`Timezone: ${storedTimeZone}`} />
    </StyledBox>
  );
};

export default BookingHeader;
